import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { ActingPostService } from '../auth/acting-post.service';
import { KeycloakService } from '../auth/keycloak.service';

/**
 * Blocks navigation until the user has confirmed an acting posting this
 * login. Synchronous and observable-free by design (spec §8.1 point 2): it
 * cannot wait, so it cannot dead-end a user behind a picker that never
 * resolves. `featureEnabled()`/`confirmed()` are plain signals derived
 * synchronously from KeycloakService's BehaviorSubject, so this is safe to
 * call before PermissionGuard's own async wait.
 */
export const actingPostGuard: CanActivateFn = (_route, state) => {
  const actingPost = inject(ActingPostService);
  const router = inject(Router);

  if (!actingPost.featureEnabled()) {
    return true;
  }
  if (actingPost.confirmed()) {
    return true;
  }
  return router.createUrlTree(['/login/posting'], { queryParams: { returnUrl: state.url } });
};

/** Guards the picker route itself: a bookmarked /login/posting URL in a
 * flag-off build bounces back to /modules instead of showing a dead picker. */
export const actingPostFeatureGuard: CanActivateFn = () => {
  const actingPost = inject(ActingPostService);
  const router = inject(Router);

  if (actingPost.featureEnabled()) {
    return true;
  }
  return router.createUrlTree(['/modules']);
};

export const actingPostReturnGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const keycloak = inject(KeycloakService);

  if (keycloak.isLoggedIn() && route.queryParamMap.has('returnUrl')) {
    keycloak.logout();
    return false;
  }

  return true;
};
